import React from 'react';
import { Modal, Button, Descriptions, Tag, Divider, Typography } from 'antd';
import moment from 'moment';
import { RequestTrackingResponse } from '@openapi';

const { Text } = Typography;

interface RequestDetailModalProps {
  visible: boolean;
  request: RequestTrackingResponse | null;
  onClose: () => void;
}

const formatTime = (ms: number): string => {
  if (ms < 1000) return `${ms.toFixed(0)}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
};

const JsonBlock: React.FC<{ value: string; bgColor: string; borderColor: string }> = ({
  value,
  bgColor,
  borderColor,
}) => {
  let content: React.ReactNode;
  try {
    const parsed = typeof value === 'string' ? JSON.parse(value) : value;
    content = (
      <pre
        style={{
          margin: 0,
          fontSize: '11px',
          fontFamily: 'Consolas, Monaco, "Courier New", monospace',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}
      >
        {JSON.stringify(parsed, null, 2)}
      </pre>
    );
  } catch {
    content = <span style={{ fontSize: '12px' }}>{String(value)}</span>;
  }

  return (
    <div
      style={{
        background: bgColor,
        border: `1px solid ${borderColor}`,
        borderRadius: '4px',
        padding: '12px',
        maxHeight: '350px',
        overflow: 'auto',
      }}
    >
      {content}
    </div>
  );
};

const RequestDetailModal: React.FC<RequestDetailModalProps> = ({ visible, request, onClose }) => {
  return (
    <Modal
      title="Request Details"
      visible={visible}
      onCancel={onClose}
      footer={[
        <Button key="close" onClick={onClose}>
          Close
        </Button>,
      ]}
      width={900}
    >
      {request && (
        <>
          {/* Section 1: Request Overview */}
          <Text strong style={{ fontSize: '14px', display: 'block', marginBottom: 8 }}>
            Request Overview
          </Text>
          <Descriptions bordered column={2} size="small">
            <Descriptions.Item label="Request ID" span={2}>
              <code style={{ fontSize: '12px' }}>{request.id}</code>
            </Descriptions.Item>
            <Descriptions.Item label="API Version">
              <Tag color={request.apiVersion === 'v2' ? 'green' : 'orange'}>{request.apiVersion}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="HTTP Method">
              <Tag color="purple">{request.httpMethod}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Endpoint" span={2}>
              <code style={{ fontSize: '12px', wordBreak: 'break-all' }}>{request.endpoint}</code>
            </Descriptions.Item>
            <Descriptions.Item label="Request Timestamp">
              {request.requestTimestamp ? moment(request.requestTimestamp).format('YYYY-MM-DD HH:mm:ss.SSS') : 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Response Timestamp">
              {request.responseTimestamp ? moment(request.responseTimestamp).format('YYYY-MM-DD HH:mm:ss.SSS') : 'N/A'}
            </Descriptions.Item>
          </Descriptions>

          <Divider style={{ margin: '16px 0' }} />

          {/* Section 2: Device Information */}
          <Text strong style={{ fontSize: '14px', display: 'block', marginBottom: 8 }}>
            Device Information
          </Text>
          <Descriptions bordered column={2} size="small">
            <Descriptions.Item label="MAC Address">
              <Tag color="blue">{request.macAddress}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Device ID">{request.deviceId || 'N/A'}</Descriptions.Item>
            {request.deviceType && <Descriptions.Item label="Device Type">{request.deviceType}</Descriptions.Item>}
            {request.manufacturer && <Descriptions.Item label="Manufacturer">{request.manufacturer}</Descriptions.Item>}
            {request.model && (
              <Descriptions.Item label="Model">
                <Tag color="purple">{request.model}</Tag>
              </Descriptions.Item>
            )}
            {request.hardwareVersion && (
              <Descriptions.Item label="Hardware Version">
                <Tag color="geekblue">{request.hardwareVersion}</Tag>
              </Descriptions.Item>
            )}
            {request.firmwareVersion && (
              <Descriptions.Item label="Firmware Version">
                <Tag color="geekblue">{request.firmwareVersion}</Tag>
              </Descriptions.Item>
            )}
            <Descriptions.Item label="Client IP">
              <Tag color="cyan">{request.clientIp || 'N/A'}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="User Agent" span={2}>
              <div style={{ fontSize: '11px', wordBreak: 'break-all' }}>{request.userAgent || 'N/A'}</div>
            </Descriptions.Item>
          </Descriptions>

          <Divider style={{ margin: '16px 0' }} />

          {/* Section 3: Performance & Status */}
          <Text strong style={{ fontSize: '14px', display: 'block', marginBottom: 8 }}>
            Performance & Status
          </Text>
          <Descriptions bordered column={2} size="small">
            <Descriptions.Item label="Response Status">
              <Tag color={request.responseStatus && request.responseStatus < 400 ? 'success' : 'error'}>
                {request.responseStatus}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Execution Time">
              <span
                style={{
                  color: (request.executionTimeMs || 0) > 5000 ? '#ff4d4f' : '#52c41a',
                  fontWeight: 'bold',
                }}
              >
                {formatTime(request.executionTimeMs || 0)}
              </span>
            </Descriptions.Item>
            <Descriptions.Item label="Device State">
              {request.deviceStatus ? (
                <Tag
                  color={
                    {
                      POSTPONED: 'orange',
                      PENDING: 'blue',
                      UP_TO_DATE: 'green',
                      NO_STATUS: 'default',
                    }[request.deviceStatus] || 'default'
                  }
                >
                  {request.deviceStatus.replace(/_/g, ' ')}
                </Tag>
              ) : (
                'N/A'
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Request Body Size">
              {request.requestBodySize !== undefined && request.requestBodySize !== null
                ? `${request.requestBodySize} bytes`
                : 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Content Type">{request.contentType || 'N/A'}</Descriptions.Item>
            {request.upgradeAvailable && (
              <Descriptions.Item label="Upgrade Available">
                <Tag color={request.upgradeAvailable === 'true' ? 'green' : 'default'}>{request.upgradeAvailable}</Tag>
              </Descriptions.Item>
            )}
          </Descriptions>

          {/* Section 4: Server Response to Device */}
          {(request.targetFirmwareVersion ||
            request.responseStatusDesc ||
            request.responseBody ||
            (request.responseBodySize !== undefined && request.responseBodySize > 0)) && (
            <>
              <Divider style={{ margin: '16px 0' }} />
              <Text strong style={{ fontSize: '14px', display: 'block', marginBottom: 8 }}>
                Server Response (sent to device)
              </Text>
              <Descriptions bordered column={2} size="small">
                {request.targetFirmwareVersion && (
                  <Descriptions.Item label="Target Firmware">
                    <Tag color="magenta">{request.targetFirmwareVersion}</Tag>
                  </Descriptions.Item>
                )}
                {request.responseBodySize !== undefined && request.responseBodySize > 0 && (
                  <Descriptions.Item label="Response Size">{request.responseBodySize} bytes</Descriptions.Item>
                )}
                {request.responseStatusDesc && (
                  <Descriptions.Item label="Status Description" span={2}>
                    <span style={{ fontSize: '12px' }}>{request.responseStatusDesc}</span>
                  </Descriptions.Item>
                )}
                {request.responseBody && (
                  <Descriptions.Item label="Response Body" span={2}>
                    <JsonBlock value={request.responseBody} bgColor="#f6ffed" borderColor="#b7eb8f" />
                  </Descriptions.Item>
                )}
              </Descriptions>
            </>
          )}

          {/* Section 5: Error Details */}
          {request.errorMessage && (
            <>
              <Divider style={{ margin: '16px 0' }} />
              <Text strong style={{ fontSize: '14px', display: 'block', marginBottom: 8, color: '#cf1322' }}>
                Error Details
              </Text>
              <div
                style={{
                  background: '#fff2f0',
                  border: '1px solid #ffccc7',
                  borderRadius: '4px',
                  padding: '12px',
                  maxHeight: '300px',
                  overflow: 'auto',
                }}
              >
                {(() => {
                  try {
                    const errorJson = JSON.parse(request.errorMessage);
                    return (
                      <pre
                        style={{
                          margin: 0,
                          fontSize: '11px',
                          fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                        }}
                      >
                        {JSON.stringify(errorJson, null, 2)}
                      </pre>
                    );
                  } catch {
                    return <span style={{ fontSize: '12px', color: '#cf1322' }}>{request.errorMessage}</span>;
                  }
                })()}
              </div>
            </>
          )}

          {/* Section 6: Device Request Payload (Additional Metadata) */}
          {request.additionalMetadata && (
            <>
              <Divider style={{ margin: '16px 0' }} />
              <Text strong style={{ fontSize: '14px', display: 'block', marginBottom: 8 }}>
                Device Request Payload
              </Text>
              <JsonBlock value={request.additionalMetadata} bgColor="#f0f5ff" borderColor="#adc6ff" />
            </>
          )}
        </>
      )}
    </Modal>
  );
};

export default RequestDetailModal;
